import React, { useMemo } from 'react';
import DeleteOutline from '@mui/icons-material/DeleteOutlined';
import Download from '@mui/icons-material/Download';
import EditNote from '@mui/icons-material/EditNote';
import FactCheck from '@mui/icons-material/FactCheckOutlined';
import Print from '@mui/icons-material/Print';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import Share from '@mui/icons-material/Share';
import useConfirm from '@core/hooks/useConfirm';
import useProfile from '@core/hooks/useProfile';
import useSnackbar from '@core/hooks/useSnackbar';
import { ViewerInfoCommand } from '@core/types';
import downloadFileFromBlobResponse from '@core/utils/downloadFileFromBlobResponse';
import { printPdfBlob } from '@core/utils/printPdf';
import {
  buildReportFileName,
  ReportAction,
  ReportActionItem,
  StudyReportItem,
} from 'app/features/reports/view';
import deleteReportFromEvodicomService from '../api/deleteReportFromEvodicomService';
import getPrintableReport from '../api/getPrintableReport';
import { getVisibleReportActions, ReportActionId } from '../utils/reportActionVisibility';

export interface UseStudyReportActionsArgs {
  /** 'shared': share-link viewer, only print / download. */
  variant: 'study' | 'shared';
  /** The active study (the reports shown belong to it). */
  studyInstanceUid: string;
  report?: StudyReportItem;
  pdf?: Blob;
  readOnly: boolean;
  isAnon: boolean;
  isAdministrator: boolean;
  /** The viewer toolbar's command handler (information / share dialogs). */
  onCommand: (commandName: ViewerInfoCommand) => void;
  onEditReport: (report: StudyReportItem) => void;
  onReportDeleted: (report: StudyReportItem) => void | Promise<void>;
  setBusy: (busy: boolean) => void;
}

const SHARED_ACTIONS: ReportActionId[] = ['print', 'download'];

/**
 * The PDF report viewer's actions: the report's own actions from the viewer
 * toolbar, with the same visibility rules. Edit / delete, the Imprimir menu,
 * then information / share.
 */
const useStudyReportActions = ({
  variant,
  studyInstanceUid,
  report,
  pdf,
  readOnly,
  isAnon,
  isAdministrator,
  onCommand,
  onEditReport,
  onReportDeleted,
  setBusy,
}: UseStudyReportActionsArgs): ReportActionItem[] => {
  const { role } = useProfile();
  const { isConfirmed } = useConfirm();
  const showSnackbar = useSnackbar();

  return useMemo(() => {
    const visible = new Set<ReportActionId>(
      variant === 'shared'
        ? SHARED_ACTIONS.filter(() => !!report)
        : getVisibleReportActions({ role, isAnon, isAdministrator, readOnly, report }),
    );

    const handleDelete = async (target: StudyReportItem) => {
      const confirmed = await isConfirmed({
        type: 'delete',
        title: '¿Eliminar este informe?',
        message: `"${target.title}" se eliminará del estudio. Esta acción no se puede deshacer.`,
      });
      if (!confirmed) return;

      setBusy(true);
      // `remove` resolves with { statusCode } instead of throwing on HTTP errors.
      const result = (await deleteReportFromEvodicomService(
        studyInstanceUid,
        target.sopInstanceUid,
      )) as { statusCode?: number } | undefined;
      setBusy(false);

      if (result?.statusCode && result.statusCode >= 400) {
        showSnackbar({
          type: 'error',
          title: 'Ocurrió un error',
          message: 'No ha sido posible eliminar el informe.',
        });
        return;
      }
      showSnackbar({
        type: 'success',
        title: 'Informe eliminado',
        message: 'El informe se eliminó del estudio.',
      });
      await onReportDeleted(target);
    };

    const handlePrintNoTemplate = async (target: StudyReportItem) => {
      setBusy(true);
      try {
        printPdfBlob(await getPrintableReport(studyInstanceUid, target.sopInstanceUid));
      } catch {
        showSnackbar({
          type: 'error',
          title: 'Ocurrió un error',
          message: 'No ha sido posible preparar el informe para imprimir.',
        });
      } finally {
        setBusy(false);
      }
    };

    const actions: Record<ReportActionId, () => ReportAction | null> = {
      edit: () =>
        report
          ? {
              id: 'edit',
              label: 'Editar reporte',
              icon: <EditNote />,
              onClick: () => onEditReport(report),
            }
          : null,
      delete: () =>
        report
          ? {
              id: 'delete',
              label: 'Eliminar informe',
              icon: <DeleteOutline />,
              color: 'error',
              onClick: () => handleDelete(report),
            }
          : null,
      print: () =>
        report
          ? {
              id: 'print',
              label: 'Imprimir',
              icon: <Print />,
              disabled: !pdf,
              onClick: () => pdf && printPdfBlob(pdf),
            }
          : null,
      'print-no-template': () =>
        report
          ? {
              id: 'print-no-template',
              label: 'Imprimir sin plantilla',
              icon: <PrintOutlined />,
              onClick: () => handlePrintNoTemplate(report),
            }
          : null,
      download: () =>
        report
          ? {
              id: 'download',
              label: 'Descargar PDF',
              icon: <Download />,
              disabled: !pdf,
              onClick: () =>
                pdf && downloadFileFromBlobResponse(pdf, buildReportFileName(report), 'pdf'),
            }
          : null,
      info: () => ({
        id: 'info',
        label: 'Información',
        icon: <FactCheck />,
        onClick: () => onCommand('info'),
      }),
      share: () => ({
        id: 'share',
        label: 'Compartir',
        icon: <Share />,
        onClick: () => onCommand('share'),
      }),
    };

    const build = (ids: ReportActionId[]) =>
      ids
        .filter((id) => visible.has(id))
        .map((id) => actions[id]())
        .filter((a): a is ReportAction => !!a);

    const group = (id: string, label: string, icon: React.ReactElement, ids: ReportActionId[]) => {
      const members = build(ids);
      return members.length ? [{ id, label, icon, actions: members }] : [];
    };

    if (variant === 'shared') {
      return build(SHARED_ACTIONS);
    }

    return [
      ...build(['edit', 'delete']),
      ...group('print', 'Imprimir', <Print />, ['print', 'print-no-template', 'download']),
      ...build(['info', 'share']),
    ];
  }, [
    variant,
    report,
    pdf,
    role,
    isAnon,
    isAdministrator,
    readOnly,
    studyInstanceUid,
    onCommand,
    onEditReport,
    onReportDeleted,
    setBusy,
    isConfirmed,
    showSnackbar,
  ]);
};

export default useStudyReportActions;
